<script setup lang="ts">
import anime from 'animejs';
import confetti, { CreateTypes } from 'canvas-confetti';
import { computed, onMounted, ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useCategories } from '@ilteducation/content-ref-data';
import { useLocale } from '@/plugins/translation';
import useQuizStore from '@/store/quiz';
import { storeToRefs } from 'pinia';
import { ExerciseWithProgress, useExerciseStore } from '@/store/exercises';

const { t, l } = useLocale();
const exerciseStore = useExerciseStore();
const { exercises: storeExercises } = storeToRefs(exerciseStore);
const quizStore = useQuizStore();
const { currentQuiz, correctAnswers } = storeToRefs(quizStore);
const route = useRoute();
const { categories } = useCategories();

const canvasRef = ref();
let canvasConfetti: CreateTypes;
const answerFeedback: { [index: number]: [string, string, string] } = {
  0: [t(l.quiz.feedback[0][0]), t(l.quiz.feedback[0][1]), t(l.quiz.feedback[0][2])],
  1: [t(l.quiz.feedback[1][0]), t(l.quiz.feedback[1][1]), t(l.quiz.feedback[1][2])],
  2: [t(l.quiz.feedback[2][0]), t(l.quiz.feedback[2][1]), t(l.quiz.feedback[2][2])],
  3: [t(l.quiz.feedback[3][0]), t(l.quiz.feedback[3][1]), t(l.quiz.feedback[3][2])],
  4: [t(l.quiz.feedback[4][0]), t(l.quiz.feedback[4][1]), t(l.quiz.feedback[4][2])],
};
const confettiOptions = {
  startVelocity: 12,
  spread: 360,
  ticks: 60,
  shapes: ['square'],
  colors: ['#56A4D7', '#CF9D58', '#596A79', '#62B878', '#BE77A6', '#D37777'],
  end: Date.now() + 20 * 1000,
};
const quizzes = computed<ExerciseWithProgress[]>(
  () => storeExercises.value[currentQuiz.value?.resourceId || ''].filter((ex) => ex.type === 'quiz') ?? [],
);
const isLastQuiz = computed(
  () => !!currentQuiz.value && quizzes.value[quizzes.value.length - 1]?.id === currentQuiz.value.id,
);

const numQuestions = computed(() => correctAnswers.value.length);
const numCorrectAnswers = computed(() => correctAnswers.value.filter((itm: any) => itm.correct === true).length);
const subject = computed(() => categories.value.find((category) => category.slug === route.params.subject));
const quizPatternBackground = `url(${
  new URL(`/src/assets/images/patterns/square/${subject.value?.color}.png`, import.meta.url).href
})`;
const backgroundColor = computed(() => `rgba(var(--v-theme-${subject.value?.color}))`);

const getFeedback = computed(() => {
  const randomFeedback = (key: number) => Math.floor(Math.random() * answerFeedback[key].length);
  const incorrectAnswers = numQuestions.value - numCorrectAnswers.value;

  if (incorrectAnswers === 0) {
    return answerFeedback[4][randomFeedback(4)];
  }

  if (incorrectAnswers === 1) {
    return answerFeedback[3][randomFeedback(3)];
  }

  if (numCorrectAnswers.value === 1) {
    return answerFeedback[1][randomFeedback(1)];
  }

  if (numCorrectAnswers.value === 0) {
    return answerFeedback[0][randomFeedback(0)];
  }

  return answerFeedback[2][randomFeedback(2)];
});

const startConfetti = () => {
  const confettiInterval = setInterval(() => {
    if (Date.now() > confettiOptions.end) {
      clearInterval(confettiInterval);
    }
    canvasConfetti({
      startVelocity: confettiOptions.startVelocity,
      spread: confettiOptions.spread,
      ticks: confettiOptions.ticks,
      colors: confettiOptions.colors,
      origin: {
        x: Math.random(),
        y: Math.random() - 0.2, // Make fireworks start higher up
      },
    });
  }, 200);
};

const scrollToResult = () => {
  const resultSplashContainer = document.querySelector('.results-splash-container');
  if (!resultSplashContainer) {
    return;
  }
  const resultElementTop = window.scrollY || window.pageYOffset + resultSplashContainer.getBoundingClientRect().top;
  const resultElementCenter = resultSplashContainer.getBoundingClientRect().height / 2;

  const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;

  if (supportsNativeSmoothScroll) {
    window.scrollTo({
      top: resultElementTop / 2 + resultElementCenter,
      behavior: 'smooth',
    });
  } else {
    window.scrollTo(0, resultElementTop / 2 + resultElementCenter);
  }
};

const separateText = () => {
  const elements = document.querySelectorAll('.js-split');

  elements.forEach((elm) => {
    elm.innerHTML = elm.innerHTML
      .split(' ')
      .map((word) => `<span>${word}</span>`)
      .join(' ');
  });
};

const animateText = () => {
  anime({
    targets: '.animation-text span',
    translateY() {
      return [anime.random(-15, 15), 0];
    },
    opacity: [0, 1],
    duration: 600,
    easing: 'easeInOutSine',
    delay: anime.stagger(60, { start: 150 }),
  });
};

onMounted(() => {
  separateText();

  if (numQuestions.value === numCorrectAnswers.value) {
    nextTick(() => {
      canvasConfetti = confetti.create(canvasRef.value, { resize: true });
      startConfetti();
    });
  }

  animateText();

  nextTick(scrollToResult);

  // Set focus to the result splash container
  const resultSplashContainer = document.getElementById('results-splash-wrapper');
  if (resultSplashContainer) {
    resultSplashContainer.focus();
  }
});
</script>
<template>
  <div
    v-if="currentQuiz"
    id="results-splash-wrapper"
    tabindex="-1"
    class="column nopad small-12 results-splash-wrapper"
  >
    <div v-if="numQuestions === numCorrectAnswers" class="full-pot">
      <div class="d-flex overflow-hidden align-center justify-center results-splash-container">
        <canvas
          id="confetti"
          ref="canvasRef"
          class="confetti"
          width="801"
          height="259"
          style="position: absolute; width: 801px; height: 259px; z-index: 10"
          aria-hidden="true"
        ></canvas>
        <div class="text text-center animation-text" style="position: relative; z-index: 20">
          <p class="quiz-result-header quiz-result-h2 js-split">
            {{ t(l.quiz.final_result, { correct: numCorrectAnswers, total: numQuestions }) }}
          </p>
          <p class="quiz-result-header quiz-result-h3 js-split">{{ getFeedback }}</p>
          <p class="quiz-result-header quiz-result-h4" v-if="!isLastQuiz">
            {{ t(l.quiz.congratulations_next_quiz) }}
          </p>
          <p class="quiz-result-header quiz-result-h4" v-else>
            {{ t(l.quiz.congratulations_all_quizzes_completed) }}
          </p>
        </div>
      </div>
    </div>
    <div v-else class="theme--light">
      <div class="d-flex overflow-hidden align-center justify-center results-splash-container subject-bg">
        <div class="pattern-quiz"></div>
        <div class="text text-center animation-text">
          <p class="quiz-result-header quiz-result-h2 js-split">
            {{ t(l.quiz.final_result, { correct: numCorrectAnswers, total: numQuestions }) }}
          </p>

          <p class="quiz-result-header quiz-result-h3 js-split">{{ getFeedback }}</p>

          <p
            class="quiz-result-header quiz-result-h4"
            v-if="currentQuiz.sort && currentQuiz.sort < quizzes.length && !currentQuiz.progress?.completed"
          >
            {{ t(l.quiz.unlocking_a_quiz_requires_all_successful_answers) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '@/foundations/theme/settings';
.results-splash-container {
  &.subject-bg {
    background: v-bind(backgroundColor);
    background-position: center;
    background-size: cover;
  }
  position: relative;
  height: 261px;
  border-radius: 4px;
  margin-bottom: 1rem;
  .animation-text {
    will-change: transform;
    max-width: 65%;
    span {
      position: relative;
      display: inline-block;
    }
  }
  .pattern-quiz {
    position: absolute;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
  }

  .quiz-result-header {
    font-family: settings.$heading-font-family;
    font-weight: 300;
    margin: 0.5rem 0 0.35em;
  }

  .quiz-result-h2 {
    font-size: 1.5rem;
  }

  .quiz-result-h3 {
    font-size: 1.15rem;
  }

  .quiz-result-h4 {
    font-size: 1rem;
  }
}

.results-splash-wrapper {
  .pattern-quiz {
    background-image: v-bind(quizPatternBackground);
  }
}

.full-pot .animation-text {
  color: rgb(var(--v-theme-on-surface));
  text-shadow:
    -3px -3px 0 #ffe,
    3px -3px 0 #fff,
    -3px 3px 0 #fff,
    3px 3px 0 #fff;
}
</style>
